import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Play, RefreshCw, BarChart3, ChevronDown, ChevronRight, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TEAM_LABELS = {
  medical_mdo: "Medisch MDO",
  legal_compliance: "Juridisch & Compliance",
  crisis_incident: "Crisis & Incident",
  construction_infra: "Bouw / Infra / Omgeving",
};

const scoreColor = (val, invert = false) => {
  // val 0..1. For DS/AA: higher = green. For SI: higher = red (invert=true).
  const good = invert ? val < 0.25 : val >= 0.7;
  const mid = invert ? val < 0.5 : val >= 0.4;
  if (good) return "text-green-600";
  if (mid) return "text-amber-600";
  return "text-red-600";
};

const Bar = ({ value, invert = false, testid }) => {
  const pct = Math.round((value || 0) * 100);
  const color = invert
    ? (value < 0.25 ? "bg-green-500" : value < 0.5 ? "bg-amber-500" : "bg-red-500")
    : (value >= 0.7 ? "bg-green-500" : value >= 0.4 ? "bg-amber-500" : "bg-red-500");
  return (
    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden" data-testid={testid}>
      <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
};

export const QualityMetricsPanel = () => {
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(null); // team_id being run
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState(null);

  const fetchLatest = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/quality/latest`);
      setSummary(r.data.summary || {});
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("[QM] latest failed:", err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  const runTeam = async (teamId) => {
    setRunning(teamId);
    setError(null);
    try {
      await axios.post(`${API}/quality/run`, { team_id: teamId });
      toast.success(`Suite voltooid voor ${TEAM_LABELS[teamId]}`);
      fetchLatest();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Suite mislukt.";
      setError(msg);
      toast.error(msg);
    } finally {
      setRunning(null);
    }
  };

  const teams = ["medical_mdo", "legal_compliance", "crisis_incident", "construction_infra"];

  return (
    <div className="bg-white border-2 border-gray-200 shadow-sm" data-testid="quality-metrics-panel">
      <div className="bg-gray-900 text-white px-4 py-3 flex items-center justify-between">
        <span className="flex items-center gap-2 font-bold text-sm" style={{ fontFamily: "Chivo, sans-serif" }}>
          <BarChart3 className="w-4 h-4" />
          Quality Metrics
        </span>
        <button
          onClick={fetchLatest}
          disabled={loading}
          className="opacity-80 hover:opacity-100 disabled:opacity-40"
          title="Vernieuwen"
          data-testid="quality-refresh"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </button>
      </div>

      <div className="p-3 space-y-3">
        <p className="text-[10px] font-mono uppercase text-gray-500 leading-relaxed">
          Disagreement (DS) ↑, Anchor adherence (AA) ↑, Sycophancy index (SI) ↓ is goed.
        </p>

        {teams.map((tid) => {
          const data = summary[tid];
          const isExpanded = expanded === tid;
          return (
            <div key={tid} className="border border-gray-200" data-testid={`team-block-${tid}`}>
              <button
                onClick={() => setExpanded(isExpanded ? null : tid)}
                className="w-full text-left px-3 py-2 flex items-center justify-between hover:bg-gray-50"
              >
                <span className="font-bold text-xs">{TEAM_LABELS[tid]}</span>
                <span className="flex items-center gap-2">
                  {data ? (
                    <>
                      <span className={`text-[11px] font-mono ${scoreColor(data.avg_ds)}`}>DS {data.avg_ds.toFixed(2)}</span>
                      <span className={`text-[11px] font-mono ${scoreColor(data.avg_aa)}`}>AA {data.avg_aa.toFixed(2)}</span>
                      <span className={`text-[11px] font-mono ${scoreColor(data.avg_si_llm, true)}`}>SI {data.avg_si_llm.toFixed(2)}</span>
                    </>
                  ) : (
                    <span className="text-[10px] text-gray-400 italic">geen data</span>
                  )}
                  {isExpanded ? <ChevronDown className="w-3 h-3 text-gray-400" /> : <ChevronRight className="w-3 h-3 text-gray-400" />}
                </span>
              </button>

              {isExpanded && (
                <div className="px-3 pb-3 pt-1 border-t border-gray-100 space-y-2">
                  {data ? (
                    <>
                      <div>
                        <p className="text-[10px] font-mono text-gray-500">Disagreement (n={data.n})</p>
                        <Bar value={data.avg_ds} testid={`bar-ds-${tid}`} />
                      </div>
                      <div>
                        <p className="text-[10px] font-mono text-gray-500">Anchor adherence</p>
                        <Bar value={data.avg_aa} testid={`bar-aa-${tid}`} />
                      </div>
                      <div>
                        <p className="text-[10px] font-mono text-gray-500">Sycophancy (LLM judge)</p>
                        <Bar value={data.avg_si_llm} invert testid={`bar-si-${tid}`} />
                      </div>
                      <div>
                        <p className="text-[10px] font-mono text-gray-500">Sycophancy (regex)</p>
                        <Bar value={data.avg_si_regex} invert testid={`bar-si-regex-${tid}`} />
                      </div>
                      <details className="text-[11px]">
                        <summary className="cursor-pointer text-gray-600 hover:text-gray-900 font-mono">{data.dilemmas?.length || 0} dilemma's</summary>
                        <div className="mt-1 space-y-1">
                          {(data.dilemmas || []).map((d) => (
                            <div key={d.dilemma_id} className="grid grid-cols-4 gap-1 font-mono text-[10px]">
                              <span className="text-gray-500">{d.dilemma_id}</span>
                              <span className={scoreColor(d.ds)}>DS {d.ds.toFixed(2)}</span>
                              <span className={scoreColor(d.aa)}>AA {d.aa.toFixed(2)}</span>
                              <span className={scoreColor(d.si_llm, true)}>SI {d.si_llm.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    </>
                  ) : (
                    <p className="text-[11px] text-gray-500 italic">Nog geen run uitgevoerd. Klik hieronder om de testset te draaien.</p>
                  )}

                  <button
                    onClick={() => runTeam(tid)}
                    disabled={running === tid}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 bg-[#0a0e1a] text-white text-[11px] font-bold hover:bg-gray-700 disabled:opacity-50 transition-colors"
                    data-testid={`run-team-${tid}`}
                  >
                    {running === tid ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Suite loopt — kan 1-2 min duren
                      </>
                    ) : (
                      <>
                        <Play className="w-3 h-3" />
                        {data ? "Opnieuw draaien" : "Start suite (5 dilemma's)"}
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {error && (
          <div className="bg-red-50 border border-red-200 p-2 flex items-start gap-2">
            <AlertCircle className="w-3 h-3 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-red-700">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};
